import React, { useEffect, useState } from 'react'
import { Card, Col, Row, Button, FormControl, Container, Spinner, Badge } from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import { FaTrashAlt, FaEdit, FaSave, FaTimes } from 'react-icons/fa'
import DTOClaveNomina from '../../../DTO/DTOClaveNomina'
import NominaServices from '../../../Services/Catalogos/Nomina'

/* ================== CONFIGURACIÓN DE COLORES (IGUAL AL REPORTE) ================== */
const INCIDENCIA_ESTILOS: any = {
    FINJ: { bg: "#dc3545", back: "#f8d7da", border: "#f5c6cb", color: "#721c24" },
    RET:  { bg: "#ffc107", back: "#fff3cd", border: "#ffeeba", color: "#856404" },
    VAC:  { bg: "#17a2b8", back: "#d1ecf1", border: "#bee5eb", color: "#0c5460" },
    PCS:  { bg: "#28a745", back: "#d4edda", border: "#c3e6cb", color: "#155124" },
    PSS:  { bg: "#fd7e14", back: "#fff3cd", border: "#ffeeba", color: "#856404" },
    LAB:  { bg: "#6c757d", back: "#f1f3f5", border: "#dee2e6", color: "#495057" },
    DFT:  { bg: "#198754", back: "#c3e6cb", border: "#155724", color: "#155124" },
    DDT:  { bg: "#198754", back: "#c3e6cb", border: "#155724", color: "#155124" },
    HE2:  { bg: "#6610f2", back: "#e0cffc", border: "#d4bdfa", color: "#212529" },
    HE3:  { bg: "#6f42c1", back: "#f5d0fe", border: "#f0abfc", color: "#212529" },
    DEFAULT: { bg: "#6c757d", back: "#f8f9fa", border: "#dee2e6", color: "#383d41" }
};

export default function CatClavesNomina() {
  const [data, setData] = useState<DTOClaveNomina[]>([])
  const [idClave, setIdClave] = useState<number | undefined>(undefined)
  const [clave, setClave] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cargarDatos = () => {
    setLoading(true)
    NominaServices.GetCatClavesNomina()
      .then(res => {
        setData(res.data)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        setError("Error al cargar datos")
      })
  }

  useEffect(() => { cargarDatos() }, [])

  const handleGuardar = () => {
    if (!clave.trim() || !descripcion.trim()) {
        setError("Complete los campos");
        return;
    }
    const accion = idClave ? 'UPDATE' : 'INSERT'
    setLoading(true)
    NominaServices.MantenimientoCatClave({ idClave, clave: clave.toUpperCase().trim(), descripcion }, accion)
      .then(() => {
        limpiarFormulario();
        cargarDatos();
      }).catch(() => {
          setLoading(false);
          setError("Error al procesar");
      })
  }

  const handleEliminar = (id: number | undefined) => {
    if (!id || !window.confirm('¿Seguro que desea eliminar esta clave?')) return
    NominaServices.MantenimientoCatClave({ idClave: id, clave: '', descripcion: '' }, 'DELETE')
        .then(() => cargarDatos())
  }

  const limpiarFormulario = () => {
    setIdClave(undefined); 
    setClave(''); 
    setDescripcion('');
    setError(null);
  }

  /* ================== COLUMNAS ================== */
  const columns = [
    {
        name: 'Preview',
        width: '100px',
        cell: (row: DTOClaveNomina) => {
          const estilo = INCIDENCIA_ESTILOS[row.clave] || INCIDENCIA_ESTILOS.DEFAULT;
          return (
            <div style={{
              backgroundColor: estilo.back,
              border: `1px solid ${estilo.border}`,
              borderRadius: '4px',
              padding: '2px',
              textAlign: 'center',
              width: '65px',
              fontSize: '9px',
              fontWeight: 'bold',
              color: estilo.color,
              lineHeight: '1'
            }}>
              <div style={{ backgroundColor: estilo.bg, color: 'white', borderRadius: '2px', marginBottom: '2px', padding: '1px' }}>
                {row.clave}
              </div>
              
            </div>
          );
        }
    },
    { name: 'Clave', selector: (row: DTOClaveNomina) => row.clave, sortable: true, width: '100px' },
    { name: 'Descripción', selector: (row: DTOClaveNomina) => row.descripcion, sortable: true },
    {
      name: 'Acciones',
      cell: (row: DTOClaveNomina) => (
        <div className="d-flex gap-1">
          <Button variant="link" size="sm" className="p-0 text-primary" onClick={() => { setIdClave(row.idClave); setClave(row.clave); setDescripcion(row.descripcion); }}> <FaEdit /> </Button>
          <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => handleEliminar(row.idClave)}> <FaTrashAlt /> </Button>
        </div>
      ),
      width: '80px'
    }
  ]

  // Color dinámico para el botón según lo que se escribe
  const estiloBoton = INCIDENCIA_ESTILOS[clave.toUpperCase()] || null;

  return (
    <Container className="mt-3" style={{ maxWidth: '950px' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="text-muted m-0 text-uppercase fw-bold" style={{ letterSpacing: '1px' }}>
            Catálogo de Conceptos de Nómina
        </h6>
        {loading && <Spinner animation="border" size="sm" variant="primary" />}
      </div>
      
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="p-2">
          <Row className="g-2">
            <Col xs={3} md={2}>
              <FormControl 
                size="sm" 
                placeholder="Clave" 
                value={clave} 
                onChange={e => setClave(e.target.value.toUpperCase())} 
                className="fw-bold"
              />
            </Col>
            <Col xs={6} md={7}>
              <FormControl 
                size="sm" 
                placeholder="Descripción del concepto" 
                value={descripcion} 
                onChange={e => setDescripcion(e.target.value)} 
              />
            </Col>
            <Col xs={3} md={3} className="d-flex gap-1">
              <Button 
                variant={idClave ? "warning" : "primary"} 
                size="sm" 
                onClick={handleGuardar} 
                disabled={loading} 
                className="w-100 fw-bold"
                style={{ 
                    backgroundColor: estiloBoton ? estiloBoton.bg : undefined,
                    borderColor: estiloBoton ? estiloBoton.bg : undefined,
                    color: estiloBoton ? (estiloBoton.bg === '#ffc107' ? 'black' : 'white') : 'white'
                }}
              >
                {idClave ? <><FaSave /> Act.</> : <><FaSave /> Add</>}
              </Button>
              {idClave && <Button variant="light" size="sm" className="border" onClick={limpiarFormulario}><FaTimes /></Button>}
            </Col>
          </Row>
          {error && <small className="text-danger d-block mt-1 fw-bold">{error}</small>}
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <DataTable
          columns={columns}
          data={data}
          pagination
          dense
          highlightOnHover
          noDataComponent={<small className="text-muted p-3">No hay registros configurados</small>}
          customStyles={customStyles}
        />
      </Card>
    </Container>
  )
}

const customStyles = {
    headCells: {
        style: {
            backgroundColor: '#f8f9fa',
            color: '#6c757d',
            fontSize: '11px',
            fontWeight: 'bold',
            textTransform: 'uppercase' as any
        }
    },
    cells: {
        style: {
            fontSize: '13px'
        }
    }
}